import { getLocaleConfig } from "configs/LangConfig"
import { DEFAULT_LOCALE, USER_LOCALE_KEY } from "hooks/useLocale"
import { interpolateTranslations } from "hooks/useTranlate/interpolateTranslations"
import { MessageDescriptor, PrimitiveType, createIntl } from "react-intl"

import { Locale } from "@shared/enums/general"
import { isLocalizedText } from "@shared/interfaces/localized"
import { isIntlKey } from "@shared/intl"
import { SerializedTranslationEntry, TranslationEntry } from "@shared/translations/translationEntry"

import { parseEnum } from "./parseEnum"

/**
 * Resolves a TranslationEntry to a display string using intl or inline localized text.
 * Used by Axios error handling (injectable formatMessage for tests).
 */
export function translateTranslationEntry(
  entry: TranslationEntry,
  formatMessage: (descriptor: MessageDescriptor, values?: Record<string, PrimitiveType>) => string
): string {
  if (isLocalizedText(entry.key)) {
    const locale = getCurrentLocale()
    return interpolateTranslations(entry.key[locale] as string, entry.data ?? {})
  }
  return formatMessage({ id: entry.key as string }, entry.data)
}

/**
 *  A function that can be used to translate messages outside our regular
 *  react flow. This is useful in cases when the react as a dependency shouldn't
 *  be provided.
 *
 * @see useTranslate for the regular translation hook
 */
export function translate(descriptor: MessageDescriptor, values?: Record<string, PrimitiveType>) {
  const currentLocale = getCurrentLocale()

  const intl = createIntl({
    ...getLocaleConfig(currentLocale),
    // We override the default on error with a custom function because
    // the createIntl from react-intl is passing a specific function
    // that throws errors in a way that they can't be caught with
    // a try-catch (maybe inside a setTimeout or something like that).
    onError: (e: Error) => {
      throw e
    },
  })

  return intl.formatMessage(descriptor, values)
}

/**
 * Attempts to translate a message string, supporting three formats:
 * 1. A serialized TranslationEntry (JSON) — deserialized and translated.
 * 2. A bare IntlKey (e.g. a Zod issue message set to a translation key) — translated directly.
 * 3. Anything else — returned as-is.
 */
export function translateMessage(message: string): string {
  try {
    const parsed = JSON.parse(message) as SerializedTranslationEntry
    const entry = TranslationEntry.fromJSON(parsed)

    // fromJSON() guarantees key is either LocalizedText or IntlKey (throws otherwise)
    if (isLocalizedText(entry.key)) {
      const locale = getCurrentLocale()
      return interpolateTranslations(entry.key[locale] as string, entry.data ?? {})
    }

    try {
      return translate({ id: entry.key as string }, entry.data)
    } catch {
      // Missing IntlKey for this locale — fall through and return the raw message below
    }
  } catch {
    // Not a serialized TranslationEntry — fall through to plain-key handling
  }

  if (isIntlKey(message)) {
    try {
      return translate({ id: message })
    } catch {
      // missing translation in current locale — fall through
    }
  }

  return message
}

/**
 * Gets the current locale from localStorage, falling back to the
 * browser-detected default. This creates a dependency on the hook's storage
 * key — ideally locale handling would live in a non-react mechanism shared
 * by hooks and utility functions.
 */
function getCurrentLocale(): Locale {
  return parseEnum(window.localStorage.getItem(USER_LOCALE_KEY), Locale, DEFAULT_LOCALE)
}
